<template>
  <div class="d-flex align-center w-100">
    <div
      v-for="main in sortedMainMenus"
      :key="main.id"
      class="menu-item"
    >
      <!-- ===================================================== -->
      <!-- 1. DROPDOWN MAIN MENU -->
      <!-- ===================================================== -->
      <!--
        hasDropDown=true always takes precedence.
        Even if isLink=true, this MainMenu is NOT navigatable.
      -->
      <v-menu
        v-if="toBoolean(main.hasDropDown)"
        v-model="openMenus[main.id]"
        open-on-hover
        offset="4"
        :open-delay="120"
        :close-delay="250"
        :close-on-content-click="false"
      >
        <!-- =================================================== -->
        <!-- MAIN MENU ACTIVATOR -->
        <!-- =================================================== -->

        <template #activator="{ props }">
          <v-btn
            v-bind="props"
            variant="text"
            class="menu-btn text-none"
            :class="{
              'menu-active': openMenus[main.id]
            }"
            :style="{
              color: theme.appBarColor,
              fontSize: '16px'
            }"
          >
            <!-- PREPEND ICON -->
            <v-icon
              v-if="
                toBoolean(main.showIcon) &&
                main.mdiIcon
              "
              start
              :icon="main.mdiIcon"
              size="20"
            />

            {{
              getLocalizedText(
                main,
                "desc",
                "descFR"
              )
            }}

            <v-icon
              end
              size="20"
            >
              mdi-menu-down
            </v-icon>
          </v-btn>
        </template>

        <!-- =================================================== -->
        <!-- MAIN DROPDOWN -->
        <!-- =================================================== -->

        <v-list
          density="compact"
          min-width="280"
          class="main-list"
          :style="{
            background: theme.appBarBg,
            color: theme.appBarColor
          }"
        >
          <template
            v-for="col in getColumnsForMain(main.id)"
            :key="col.colId"
          >
            <!-- ================================================= -->
            <!-- COLUMN WITH CHILD DROPDOWN -->
            <!-- ================================================= -->

            <v-menu
              v-if="
                toBoolean(col.hasDropDown) &&
                getItemsForColumn(col.colId).length > 0
              "
              open-on-hover
              location="end"
              :open-delay="100"
              :close-delay="200"
              :close-on-content-click="true"
            >
              <template #activator="{ props: columnProps }">
                <v-list-item
                  v-bind="columnProps"
                  class="nested-activator"
                >
                  <template #prepend>
                    <v-icon
                      v-if="col.mdiIcon"
                      :icon="col.mdiIcon"
                      size="24"
                    />
                  </template>

                  <v-list-item-title>
                    {{
                      getLocalizedText(
                        col,
                        "desc",
                        "descFR"
                      )
                    }}
                  </v-list-item-title>

                  <template #append>
                    <v-icon size="small">
                      mdi-menu-right
                    </v-icon>
                  </template>
                </v-list-item>
              </template>

              <!-- ============================================= -->
              <!-- CHILD ITEMS -->
              <!-- ============================================= -->

              <v-list
                density="compact"
                min-width="320"
                class="child-list"
                :style="{
                  background: theme.appBarBg,
                  color: theme.appBarColor
                }"
              >
                <template
                  v-for="item in getItemsForColumn(col.colId)"
                  :key="item.id"
                >
                  <!-- CLICKABLE ITEM -->
                  <component
                    :is="
                      getMenuHref(item)
                        ? 'a'
                        : 'router-link'
                    "
                    v-if="isMenuItemClickable(item)"
                    :to="getMenuRoute(item)"
                    :href="getMenuHref(item)"
                    :target="
                      getMenuHref(item)
                        ? '_blank'
                        : undefined
                    "
                    :rel="
                      getMenuHref(item)
                        ? 'noopener noreferrer'
                        : undefined
                    "
                    class="
                      nested-item-link
                      no-link-style
                    "
                    @click="
                      closeMainMenu(
                        main.id
                      )
                    "
                  >
                    <v-list-item
                      class="menu-list-item"
                    >
                      <template #prepend>
                        <v-icon
                          v-if="item.mdiIcon"
                          :icon="item.mdiIcon"
                          size="24"
                        />
                      </template>

                      <v-list-item-title>
                        {{
                          getLocalizedText(
                            item,
                            "mainDesc",
                            "mainDescFR"
                          )
                        }}
                      </v-list-item-title>

                      <v-list-item-subtitle>
                        {{
                          getLocalizedText(
                            item,
                            "subDesc",
                            "subDescFR"
                          )
                        }}
                      </v-list-item-subtitle>
                    </v-list-item>
                  </component>

                  <!-- NON CLICKABLE ITEM -->
                  <v-list-item
                    v-else
                    disabled
                    class="menu-list-item"
                  >
                    <template #prepend>
                      <v-icon
                        v-if="item.mdiIcon"
                        :icon="item.mdiIcon"
                        size="24"
                      />
                    </template>

                    <v-list-item-title>
                      {{
                        getLocalizedText(
                          item,
                          "mainDesc",
                          "mainDescFR"
                        )
                      }}
                    </v-list-item-title>

                    <v-list-item-subtitle>
                      {{
                        getLocalizedText(
                          item,
                          "subDesc",
                          "subDescFR"
                        )
                      }}
                    </v-list-item-subtitle>
                  </v-list-item>
                </template>
              </v-list>
            </v-menu>

            <!-- ================================================= -->
            <!-- COLUMN WITHOUT CHILD DROPDOWN -->
            <!-- ================================================= -->

            <v-list-item
              v-else
              class="nested-activator"
            >
              <template #prepend>
                <v-icon
                  v-if="col.mdiIcon"
                  :icon="col.mdiIcon"
                  size="24"
                />
              </template>

              <v-list-item-title>
                {{
                  getLocalizedText(
                    col,
                    "desc",
                    "descFR"
                  )
                }}
              </v-list-item-title>
            </v-list-item>
          </template>

          <!-- EMPTY DROPDOWN -->
          <v-list-item
            v-if="
              getColumnsForMain(main.id).length === 0
            "
            disabled
          >
            <v-list-item-title>
              {{
                currentLanguage === "fr"
                  ? "Aucun élément disponible"
                  : "No items available"
              }}
            </v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>

      <!-- ===================================================== -->
      <!-- 2. DIRECT MAIN MENU LINK -->
      <!-- ===================================================== -->
      <!--
        MainMenu navigation rule:

        hasDropDown = true
          => never a direct link

        hasDropDown = false
          => navigate when getMenuRoute(main)
             or getMenuHref(main) resolves

        DynamicPageId takes priority.
        DynamicPage owns the public URL.

        Route resolution remains centralized
        in the shared getMenuRoute() function.
      -->

      <component
        :is="
          getMenuHref(main)
            ? 'a'
            : 'router-link'
        "
        v-else-if="
          isMainMenuNavigatable(
            main
          )
        "
        :to="
          getMenuRoute(
            main
          )
        "
        :href="
          getMenuHref(
            main
          )
        "
        :target="
          toBoolean(
            main.openInNewTab
          )
            ? '_blank'
            : undefined
        "
        :rel="
          toBoolean(
            main.openInNewTab
          )
            ? 'noopener noreferrer'
            : undefined
        "
        class="
          main-menu-link
          no-link-style
        "
        :style="{
          color: theme.appBarColor
        }"
      >
        <v-btn
          variant="text"
          class="menu-btn text-none"
          :style="{
            color: theme.appBarColor,
            fontSize: '16px'
          }"
        >
          <!-- PREPEND ICON -->
          <v-icon
            v-if="
              toBoolean(main.showIcon) &&
              main.mdiIcon
            "
            start
            :icon="main.mdiIcon"
            size="20"
          />

          {{
            getLocalizedText(
              main,
              "desc",
              "descFR"
            )
          }}
        </v-btn>
      </component>

      <!-- ===================================================== -->
      <!-- 3. NON-LINK / NON-DROPDOWN MAIN MENU -->
      <!-- ===================================================== -->

      <v-btn
        v-else
        variant="text"
        class="
          menu-btn
          text-none
          main-menu-disabled
        "
        :style="{
          color: theme.appBarColor,
          fontSize: '16px'
        }"
      >
        <v-icon
          v-if="
            toBoolean(main.showIcon) &&
            main.mdiIcon
          "
          start
          :icon="main.mdiIcon"
          size="20"
        />

        {{
          getLocalizedText(
            main,
            "desc",
            "descFR"
          )
        }}
      </v-btn>
    </div>
  </div>

  <!-- ======================================================= -->
  <!-- LOAD ERROR -->
  <!-- ======================================================= -->

  <v-snackbar
    :model-value="Boolean(loadError)"
    color="error"
    timeout="8000"
  >
    {{ loadError }}
  </v-snackbar>
</template>

<script setup>
import {
  computed,
  onMounted,
  ref,
  watch
} from "vue";

import {
  useI18n
} from "vue-i18n";

import publicApi
  from "@/auth/publicApi";

import {
  useLangStore
} from "@/store/lang";

import {
  useThemeStore
} from "@/store/auth";

// ============================================================
// STORES
// ============================================================

const lang =
  useLangStore();

const themeStore =
  useThemeStore();

const {
  locale
} = useI18n();

// ============================================================
// DATA
// ============================================================

const megaMainMenu =
  ref([]);

const megaColumn =
  ref([]);

const megaColumnItem =
  ref([]);

const megaColumnImage =
  ref([]);

const dynamicPages =
  ref([]);

// ============================================================
// STATE
// ============================================================

const loading =
  ref(false);

const loadError =
  ref("");

const openMenus =
  ref({});

// ============================================================
// LANGUAGE
// ============================================================

const currentLanguage =
  computed(
    () =>
      lang.currentLanguage ||
      "en"
  );

watch(
  currentLanguage,
  newLanguage => {
    locale.value =
      newLanguage ||
      "en";
  },
  {
    immediate: true
  }
);

// ============================================================
// THEME
// ============================================================

const theme =
  computed(
    () =>
      themeStore.themeStyles ||
      {
        appBarBg:
          "#ffffff",

        appBarColor:
          "#000000"
      }
  );

// ============================================================
// NORMALIZE ARRAY
// ============================================================

function normalizeArray(
  data
) {
  if (
    Array.isArray(data)
  ) {
    return data;
  }

  if (
    Array.isArray(
      data?.data
    )
  ) {
    return data.data;
  }

  if (
    Array.isArray(
      data?.items
    )
  ) {
    return data.items;
  }

  if (
    Array.isArray(
      data?.results
    )
  ) {
    return data.results;
  }

  if (
    Array.isArray(
      data?.value
    )
  ) {
    return data.value;
  }

  return [];
}

// ============================================================
// BOOLEAN NORMALIZATION
// ============================================================

function toBoolean(
  value
) {
  if (
    typeof value ===
    "boolean"
  ) {
    return value;
  }

  if (
    typeof value ===
    "number"
  ) {
    return value === 1;
  }

  if (
    typeof value ===
    "string"
  ) {
    return [
      "true",
      "1",
      "yes",
      "on"
    ].includes(
      value
        .trim()
        .toLowerCase()
    );
  }

  return false;
}

// ============================================================
// ID COMPARISON
// ============================================================

function sameId(
  firstId,
  secondId
) {
  if (
    firstId == null ||
    secondId == null
  ) {
    return false;
  }

  return (
    String(firstId)
      .trim() ===
    String(secondId)
      .trim()
  );
}

// ============================================================
// MAIN MENU SORTING
// ============================================================

const sortedMainMenus =
  computed(
    () =>
      [...megaMainMenu.value]
        .filter(
          main =>
            main?.isVisible !== false
        )
        .sort(
          (
            first,
            second
          ) =>
            Number(
              first?.colOrder ||
              0
            ) -
            Number(
              second?.colOrder ||
              0
            )
        )
  );

// ============================================================
// COLUMNS
// ============================================================

function getColumnsForMain(
  mainMenuId
) {
  return megaColumn.value
    .filter(
      column =>
        sameId(
          column.mainMenuId,
          mainMenuId
        )
    )
    .sort(
      (
        first,
        second
      ) =>
        Number(
          first?.colOrder ||
          0
        ) -
        Number(
          second?.colOrder ||
          0
        )
    );
}

// ============================================================
// ITEMS
// ============================================================

function getItemsForColumn(
  columnId
) {
  return megaColumnItem.value
    .filter(
      item =>
        sameId(
          item.colId,
          columnId
        )
    )
    .sort(
      (
        first,
        second
      ) =>
        Number(
          first?.menuorder ||
          0
        ) -
        Number(
          second?.menuorder ||
          0
        )
    );
}

// ============================================================
// LOCALIZED TEXT
// ============================================================

function getLocalizedText(
  record,
  englishProperty,
  frenchProperty
) {
  if (!record) {
    return "";
  }

  if (
    currentLanguage.value ===
    "fr"
  ) {
    return (
      record[frenchProperty] ||
      record[englishProperty] ||
      ""
    );
  }

  return (
    record[englishProperty] ||
    record[frenchProperty] ||
    ""
  );
}

// ============================================================
// CLEAN VALUE
// ============================================================

function cleanValue(
  value
) {
  const normalized =
    String(
      value ??
      ""
    )
      .trim();

  if (
    !normalized ||
    normalized === "#" ||
    normalized
      .toLowerCase() ===
      "null" ||
    normalized
      .toLowerCase() ===
      "undefined"
  ) {
    return "";
  }

  return normalized;
}

// ============================================================
// EXTERNAL URL
// ============================================================

function isExternalUrl(
  value
) {
  return /^(https?:)?\/\//i
    .test(
      cleanValue(
        value
      )
    );
}

// ============================================================
// DYNAMIC PAGE LOOKUP
// ============================================================

function getDynamicPage(
  item
) {
  if (
    !item?.dynamicPageId
  ) {
    return undefined;
  }

  return dynamicPages.value
    .find(
      page =>
        sameId(
          page.id,
          item.dynamicPageId
        )
    );
}

// ============================================================
// DYNAMIC PAGE SLUG
// ============================================================

function getDynamicPageSlug(
  item
) {
  const page =
    getDynamicPage(
      item
    );

  if (!page) {
    return undefined;
  }

  if (
    currentLanguage.value ===
    "fr"
  ) {
    return cleanValue(
      page.slugFr ??
      page.slugEn ??
      ""
    );
  }

  return cleanValue(
    page.slugEn ??
    page.slugFr ??
    ""
  );
}

// ============================================================
// STATIC ROUTE VALUE
// ============================================================

function getStaticRouteValue(
  item
) {
  if (
    currentLanguage.value ===
    "fr"
  ) {
    return cleanValue(
      item?.menuPathFr ||
      item?.menuPathEn ||
      item?.linkPathFr ||
      item?.linkPath
    );
  }

  return cleanValue(
    item?.menuPathEn ||
    item?.menuPathFr ||
    item?.linkPath ||
    item?.linkPathFr
  );
}

// ============================================================
// EXTERNAL LINK VALUE
// ============================================================

function getExternalLink(
  item
) {
  if (
    currentLanguage.value ===
    "fr"
  ) {
    return cleanValue(
      item?.linkPathFr ||
      item?.linkPath
    );
  }

  return cleanValue(
    item?.linkPath ||
    item?.linkPathFr
  );
}

// ============================================================
// ROUTER DESTINATION
// ============================================================

function getMenuRoute(
  item
) {
  if (!item) {
    return undefined;
  }

  // ----------------------------------------------------------
  // DYNAMIC PAGE
  // ----------------------------------------------------------

  if (
    item.dynamicPageId
  ) {
    const page =
      getDynamicPage(
        item
      );

    if (!page) {
      return undefined;
    }

    if (
      page.isPublished ===
      false
    ) {
      return undefined;
    }

    const slug =
      getDynamicPageSlug(
        item
      );

    /*
     * undefined means the DynamicPage
     * could not be resolved.
     *
     * An empty string is valid because
     * it represents the homepage:
     *
     * /en/
     * /fr/
     */
    if (
      slug ===
      undefined
    ) {
      return undefined;
    }

    const pagePath =
      slug
        ? slug
            .split("/")
            .map(
              segment =>
                segment.trim()
            )
            .filter(
              Boolean
            )
        : [];

    return {
      name:
        "DynamicPublicPage",

      params: {
        lang:
          currentLanguage.value,

        pagePath
      }
    };
  }

  // ----------------------------------------------------------
  // STATIC ROUTE
  // ----------------------------------------------------------

  const routeValue =
    getStaticRouteValue(
      item
    );

  if (
    !routeValue ||
    isExternalUrl(
      routeValue
    )
  ) {
    return undefined;
  }

  if (
    routeValue.startsWith(
      "/"
    )
  ) {
    return {
      path:
        routeValue
    };
  }

  return {
    name:
      routeValue
  };
}

// ============================================================
// EXTERNAL HREF
// ============================================================

function getMenuHref(
  item
) {
  if (
    !item ||
    item.dynamicPageId
  ) {
    return undefined;
  }

  const link =
    getExternalLink(
      item
    );

  return isExternalUrl(
    link
  )
    ? link
    : undefined;
}

// ============================================================
// CHILD ITEM CLICKABILITY
// ============================================================

function isMenuItemClickable(
  item
) {
  if (!item) {
    return false;
  }

  /*
   * DynamicPageId is an explicit
   * navigation relationship.
   */
  if (
    item.dynamicPageId
  ) {
    return Boolean(
      getMenuRoute(
        item
      )
    );
  }

  /*
   * Static / external items honor
   * IsLink when it is supplied.
   */
  if (
    item.isLink !==
      undefined &&
    item.isLink !==
      null &&
    !toBoolean(
      item.isLink
    )
  ) {
    return false;
  }

  return Boolean(
    getMenuRoute(
      item
    ) ||
    getMenuHref(
      item
    )
  );
}

// ============================================================
// MAIN MENU DIRECT LINK RULE
// ============================================================

function isMainMenuNavigatable(
  main
) {
  if (!main) {
    return false;
  }

  /*
   * Dropdown always takes precedence.
   * A dropdown MainMenu is a container,
   * never a direct navigation link.
   */
  if (
    toBoolean(
      main.hasDropDown
    )
  ) {
    return false;
  }

  /*
   * For non-dropdown MainMenu records,
   * navigate whenever a real destination
   * resolves.
   *
   * DynamicPageId is handled first by
   * getMenuRoute().
   */
  return Boolean(
    getMenuRoute(
      main
    ) ||
    getMenuHref(
      main
    )
  );
}

// ============================================================
// CLOSE MAIN MENU
// ============================================================

function closeMainMenu(
  mainMenuId
) {
  if (
    mainMenuId == null
  ) {
    return;
  }

  openMenus.value[
    mainMenuId
  ] = false;
}

// ============================================================
// LOAD MENU
// ============================================================

async function loadMegaMenu() {
  loading.value =
    true;

  loadError.value =
    "";

  try {
    const [
      mainMenuResponse,
      columnResponse,
      itemResponse,
      imageResponse,
      dynamicPageResponse
    ] =
      await Promise.all([
        publicApi.get(
          "/MainMenu"
        ),

        publicApi.get(
          "/MegamenuColumn"
        ),

        publicApi.get(
          "/MegamenuItems"
        ),

        publicApi.get(
          "/MegamenuImages"
        ),

        publicApi.get(
          "/dynamic-pages"
        )
      ]);


    megaMainMenu.value =
      normalizeArray(
        mainMenuResponse.data
      );

    megaColumn.value =
      normalizeArray(
        columnResponse.data
      );

    megaColumnItem.value =
      normalizeArray(
        itemResponse.data
      );

    megaColumnImage.value =
      normalizeArray(
        imageResponse.data
      );

    dynamicPages.value =
      normalizeArray(
        dynamicPageResponse.data
      );
  }
  catch (error) {
    console.error(
      "Unable to load mega menu:",
      error
    );

    loadError.value =
      error?.response
        ?.data
        ?.message ||
      error?.response
        ?.data
        ?.detail ||
      error?.message ||
      "Unable to load the menu.";
  }
  finally {
    loading.value =
      false;
  }
}

// ============================================================
// INITIALIZE
// ============================================================

onMounted(
  loadMegaMenu
);
</script>

<style scoped>
/* ============================================================
   TOP LEVEL
============================================================ */

.menu-item {
  display: flex;

  align-items: center;

  margin-right: 4px;
}

.menu-btn {
  position: relative;

  min-width: 0;

  overflow: visible;

  font-weight: 400;
}

.menu-btn
:deep(.v-btn__content) {
  overflow: hidden;

  text-overflow: ellipsis;

  white-space: nowrap;
}

/* ============================================================
   PREPEND ICON
============================================================ */

.menu-btn
:deep(.v-icon--start) {
  margin-inline-end: 7px;
}

/* ============================================================
   MAIN MENU DIRECT LINK
============================================================ */

.main-menu-link {
  display: inline-flex;

  align-items: center;

  color: inherit;

  text-decoration: none;
}

.main-menu-link:hover {
  color: inherit;

  text-decoration: none;
}

/* ============================================================
   DISABLED TOP MENU
============================================================ */

.main-menu-disabled {
  cursor: default;
}

/* ============================================================
   ACTIVE DROPDOWN
============================================================ */

.menu-btn:hover,
.menu-active {
  border-bottom:
    2px solid red;
}

/* ============================================================
   MAIN / CHILD LISTS
============================================================ */

.main-list,
.child-list {
  margin-top: 20px;

  border-radius: 8px;
}

/* ============================================================
   NESTED COLUMN
============================================================ */

.nested-activator {
  min-height: 54px;
}

.nested-activator:hover,
.menu-list-item:hover {
  background-color:
    rgba(
      0,
      0,
      0,
      0.04
    );

  cursor: pointer;
}

/* ============================================================
   NESTED LINK
============================================================ */

.nested-item-link {
  display: block;

  color: inherit;

  text-decoration: none;
}

.no-link-style {
  color: inherit;

  text-decoration: none;
}

/* ============================================================
   CHILD ITEM
============================================================ */

.menu-list-item {
  margin-bottom: 8px;
}

/* ============================================================
   RESPONSIVE
============================================================ */

@media (
  max-width: 960px
) {
  .main-list {
    min-width: min(
      280px,
      90vw
    ) !important;
  }

  .child-list {
    min-width: min(
      320px,
      90vw
    ) !important;
  }
}
</style>